import { type Channel, type Membership, TimetokenUtils } from '@pubnub/chat';
import { toast } from 'sonner';
import { create, type StoreApi } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { AM_XL_WIDTH_PX, LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import { type Conversation } from '@avanta/shared/types/chat';
import { fetchConversationsFromSDK } from '@avanta/shared/utils/chat';
import { useMessagesStore } from './messages';
import { useChatStore } from '.';

function getDefaultIsOpenInfoFromViewport(): boolean {
  if (typeof document === 'undefined') return true;
  return document.documentElement.clientWidth > AM_XL_WIDTH_PX;
}

interface ConversationsStore {
  conversations: Map<string, Conversation>;
  activeChannelId: string | null;
  isLoading: boolean;
  isLoadingConversation: boolean;
  isCreatingConversation: boolean;
  isTyping: boolean;
  isOtherUserOnline: boolean;
  isOpenInfo: boolean;
  isSelectionMode: boolean;
  selectedChannelIds: Set<string>;
  setActiveChannel: (channelId: string | null) => void;
  setIsOpenInfo: (value: boolean) => void;
  setIsTyping: (value: boolean) => void;
  setIsOtherUserOnline: (value: boolean) => void;
  toggleSelectionMode: () => void;
  toggleChannelSelection: (channelId: string) => void;
  archiveSelection: (channelIds: Set<string>) => Promise<void>;
  unarchiveSelection: (channelIds: Set<string>) => Promise<void>;
  leaveSelection: (channelIds: Set<string>) => Promise<void>;
  loadConversations: () => Promise<void>;
  createConversation: (userId: string, onError?: () => void) => Promise<Channel | undefined | null>;
  addConversation: (conversation: Conversation) => void;
  leaveConversation: (channelId: string) => Promise<void>;
  archiveConversation: (channelId: string) => Promise<void>;
  unarchiveConversation: (channelId: string) => Promise<void>;
  updateUnreadCount: (channelId: string, delta: number, messageTimetoken?: string) => void;
  resetUnreadCount: (channelId: string) => void;
}

type SetState = StoreApi<ConversationsStore>['setState'];
type GetState = StoreApi<ConversationsStore>['getState'];

type MembershipCustomRecord = Record<string, string | number | boolean | null>;

const cloneConversations = (state: ConversationsStore): Map<string, Conversation> =>
  new Map(state.conversations);

const resetSelectionState = (): Pick<
  ConversationsStore,
  'isSelectionMode' | 'selectedChannelIds'
> => ({
  isSelectionMode: false,
  selectedChannelIds: new Set<string>(),
});

const buildUpdatedChannels = (
  memberships: Membership[],
  channelIds: Set<string>,
  archivedValue: boolean
): Array<{ id: string; custom: MembershipCustomRecord }> =>
  memberships.map(membership => {
    const existingCustom = (membership.custom as MembershipCustomRecord | null) ?? {};
    return {
      id: membership.channel.id,
      custom: {
        ...existingCustom,
        archived: channelIds.has(membership.channel.id)
          ? archivedValue
          : ((existingCustom.archived as boolean | undefined) ?? false),
      },
    };
  });

const loadConversations = (set: SetState) => async (): Promise<void> => {
  set({ isLoading: true });
  try {
    const conversations = await fetchConversationsFromSDK();
    const conversationsMap = new Map(
      conversations.map(conversation => [conversation.channel.id, conversation])
    );
    set(state => {
      const { activeChannelId } = state;
      const isActiveStillValid = activeChannelId && conversationsMap.has(activeChannelId);

      // Preserve locally-cleared unread counts so that a reload with stale server
      // data doesn't cause a notification flash. setLastReadMessageTimetoken is
      // async; the server may still report > 0 until it propagates.
      for (const [channelId, localConversation] of state.conversations) {
        const wasLocallyCleared =
          localConversation.unreadCount === 0 && localConversation.lastReadAt !== undefined;
        if (!wasLocallyCleared) continue;
        const fresh = conversationsMap.get(channelId);
        if (!fresh) continue;
        conversationsMap.set(channelId, {
          ...fresh,
          unreadCount: 0,
          lastReadAt: localConversation.lastReadAt,
        });
      }

      return {
        conversations: conversationsMap,
        activeChannelId: isActiveStillValid ? activeChannelId : null,
      };
    });
  } catch (error) {
    toast.error(String(error));
  } finally {
    set({ isLoading: false });
  }
};

/**
 * Finds an already-open (non-closed) direct membership between the current user and
 * the given participant, if any. All direct channels for a pair share the
 * `direct.<sortedUserIds>` prefix — the default PubNub-generated id has no suffix,
 * while ones recreated after a closed channel append `.<suffix>` — so matching on the
 * prefix covers every channel ever created for that pair, not just the default one.
 */
const findOpenDirectMembership = (
  memberships: Membership[],
  directChannelPrefix: string
): Membership | undefined =>
  memberships.find(membership => {
    const channel = membership.channel;
    if (!channel?.id.startsWith(directChannelPrefix)) return false;
    return (channel.custom as Record<string, unknown> | null)?.closed !== true;
  });

const createConversation =
  (set: SetState) =>
  async (userId: string, onError?: () => void): Promise<Channel | undefined | null> => {
    const { chat } = useChatStore.getState();
    if (!chat) return null;
    set({ isCreatingConversation: true });
    try {
      const directChannelPrefix = `direct.${[chat.currentUser.id, userId].sort().join('-')}`;
      const { memberships } = await chat.currentUser.getMemberships();
      const openMembership = findOpenDirectMembership(memberships, directChannelPrefix);

      let channel: Channel;
      if (openMembership?.channel) {
        // Reuse the already-open conversation for this pair instead of creating a new
        // one every time: there should only ever be one open channel per user pair.
        channel = openMembership.channel;
      } else {
        const user = await chat.getUser(userId);
        if (!user) throw new Error();
        ({ channel } = await chat.createDirectConversation({ user }));

        if (channel.custom?.closed === true) {
          // The default channel for this user pair is closed: undo the accidental
          // join and open a fresh channel with an explicit id to avoid reusing the
          // closed conversation history.
          await channel.leave();
          // PubNub App Context channel IDs are capped at 92 UTF-8 bytes, so the
          // timestamp is base36-encoded to keep `direct.<uuid>-<uuid>.<suffix>` under
          // that limit (two full UUIDs already take up 73 characters).
          ({ channel } = await chat.createDirectConversation({
            user,
            channelId: `${directChannelPrefix}.${Date.now().toString(36)}`,
          }));
        }
      }

      const { members } = await channel.getMembers();
      const otherUser = members.find(member => member.user?.id !== chat.currentUser.id)?.user;
      if (!otherUser) return channel;

      set(state => {
        const current = state.conversations.get(channel.id);
        const next = new Map(state.conversations);
        next.set(channel.id, {
          ...current,
          channel,
          otherUser,
          membership: current?.membership ?? openMembership,
          unreadCount: current?.unreadCount ?? 0,
          lastMessageAt: current?.lastMessageAt,
        });
        return { conversations: next, activeChannelId: channel.id };
      });
      void useMessagesStore.getState().loadMessages(channel.id);
      return channel;
    } catch {
      toast.error(t('ChatStore.conversations.error', { ns: LOCIZE_NS.CHAT }));
      onError?.();
    } finally {
      set({ isCreatingConversation: false });
    }
  };

const leaveConversation =
  (set: SetState) =>
  async (channelId: string): Promise<void> => {
    const { chat } = useChatStore.getState();
    if (!chat || !channelId) return;
    try {
      const channel = await chat.getChannel(channelId);
      if (!channel) return;
      await channel.update({
        custom: { ...((channel.custom as Record<string, unknown> | null) ?? {}), closed: true },
      });
      await channel.leave();
      set(state => {
        const next = new Map(state.conversations);
        next.delete(channelId);
        return {
          conversations: next,
          activeChannelId: state.activeChannelId === channelId ? null : state.activeChannelId,
        };
      });
    } catch {
      toast.error(t('ChatStore.conversations.leaveChat.error', { ns: LOCIZE_NS.CHAT }));
    }
  };

const setArchivedFlag =
  (set: SetState, get: GetState) =>
  async (channelId: string, archived: boolean): Promise<void> => {
    const conversation = get().conversations.get(channelId);
    if (!conversation) return;

    const membership = await resolveMembership(set, channelId, conversation);
    if (!membership) return;

    const updated = await membership.update({
      custom: { ...((membership.custom as Record<string, unknown> | null) ?? {}), archived },
    });

    set(state => {
      const current = state.conversations.get(channelId);
      if (!current) return state;
      const next = new Map(state.conversations);
      next.set(channelId, { ...current, membership: updated });
      return {
        conversations: next,
        activeChannelId:
          archived && state.activeChannelId === channelId ? null : state.activeChannelId,
      };
    });
  };

const archiveConversation =
  (set: SetState, get: GetState) =>
  async (channelId: string): Promise<void> => {
    try {
      await setArchivedFlag(set, get)(channelId, true);
    } catch {
      toast.error(t('ChatStore.conversations.archive.error', { ns: LOCIZE_NS.CHAT }));
    }
  };

const unarchiveConversation =
  (set: SetState, get: GetState) =>
  async (channelId: string): Promise<void> => {
    try {
      await setArchivedFlag(set, get)(channelId, false);
    } catch {
      toast.error(t('ChatStore.conversations.unarchive.error', { ns: LOCIZE_NS.CHAT }));
    }
  };

const updateUnreadCount =
  (set: SetState, get: GetState) =>
  (channelId: string, delta: number, messageTimetoken?: string): void => {
    const currentConversation = get().conversations.get(channelId);
    const isArchived = currentConversation?.membership?.custom?.archived === true;

    set(state => {
      const conv = state.conversations.get(channelId);
      if (!conv) return state;
      const next = new Map(state.conversations);
      next.set(channelId, {
        ...conv,
        unreadCount: (conv.unreadCount ?? 0) + delta,
        ...(messageTimetoken
          ? { lastMessageAt: TimetokenUtils.timetokenToDate(messageTimetoken).getTime() }
          : {}),
      });
      return { conversations: next };
    });

    if (messageTimetoken && isArchived && currentConversation) {
      void (async (): Promise<void> => {
        const membership = await resolveMembership(set, channelId, currentConversation);
        if (!membership) return;
        try {
          const updated = await membership.update({
            custom: {
              ...((membership.custom as Record<string, unknown> | null) ?? {}),
              archived: false,
            },
          });
          set(state => {
            const current = state.conversations.get(channelId);
            if (!current) return state;
            const next = new Map(state.conversations);
            next.set(channelId, { ...current, membership: updated });
            return { conversations: next };
          });
        } catch {
          // ignore non-numeric (optimistic) timetokens
        }
      })();
    }
  };

const getLatestReadTimetoken = (channelId: string): string => {
  const messages = useMessagesStore.getState().messages.get(channelId) ?? [];
  let latest = 0n;
  for (const message of messages) {
    try {
      // Pending messages still have a non-numeric (temp) timetoken and throw here,
      // so they're skipped; already-sent ones (numeric timetoken) are included
      // even if they keep their tempId until the realtime echo reconciles them.
      const timetoken = BigInt(message.timetoken);
      if (timetoken > latest) latest = timetoken;
    } catch {
      // ignore non-numeric (optimistic) timetokens
    }
  }
  // PubNub timetoken = ms * 10000 (10 nanoseconds units)
  if (latest === 0n) return String(Date.now() * 10000);
  return (latest + 1n).toString();
};

/**
 * Returns the membership for a channel, lazily fetching and caching it when the
 * conversation was created/invited locally (and therefore has no membership).
 */
const resolveMembership = async (
  set: SetState,
  channelId: string,
  conversation: Conversation
): Promise<Membership | undefined> => {
  if (conversation.membership) return conversation.membership;
  const { chat } = useChatStore.getState();
  if (!chat) return undefined;

  const { memberships } = await chat.currentUser.getMemberships();
  const membership = memberships.find(item => item.channel?.id === channelId);
  if (!membership) return undefined;

  set(state => {
    const current = state.conversations.get(channelId);
    if (!current) return state;
    const next = new Map(state.conversations);
    next.set(channelId, { ...current, membership });
    return { conversations: next };
  });
  return membership;
};

const resetUnreadCount =
  (set: SetState, get: GetState) =>
  (channelId: string): void => {
    set(state => {
      const conv = state.conversations.get(channelId);
      if (!conv || conv.unreadCount === 0) return state;
      const next = new Map(state.conversations);
      next.set(channelId, { ...conv, unreadCount: 0, lastReadAt: Date.now() });
      return { conversations: next };
    });

    const conversation = get().conversations.get(channelId);
    if (!conversation) return;

    void (async (): Promise<void> => {
      const membership = await resolveMembership(set, channelId, conversation);
      if (!membership) return;
      try {
        await membership.setLastReadMessageTimetoken(getLatestReadTimetoken(channelId));
      } catch {
        // silent — the local count is already 0
      }
    })();
  };

const toggleSelectionMode = (set: SetState) => (): void => {
  set(state => ({
    isSelectionMode: !state.isSelectionMode,
    selectedChannelIds: new Set<string>(),
  }));
};

const toggleChannelSelection =
  (set: SetState) =>
  (channelId: string): void => {
    set(state => {
      const next = new Set(state.selectedChannelIds);
      if (next.has(channelId)) {
        next.delete(channelId);
      } else {
        next.add(channelId);
      }
      return { selectedChannelIds: next };
    });
  };

const archiveSelection =
  (set: SetState) =>
  async (channelIds: Set<string>): Promise<void> => {
    const { chat } = useChatStore.getState();
    if (!chat || channelIds.size === 0) return;

    try {
      const { memberships } = await chat.currentUser.getMemberships();

      await chat.sdk.objects.setMemberships({
        channels: buildUpdatedChannels(memberships, channelIds, true),
      });

      const { memberships: freshMemberships } = await chat.currentUser.getMemberships();

      set(state => {
        const next = cloneConversations(state);
        const newActiveChannelId =
          state.activeChannelId && channelIds.has(state.activeChannelId)
            ? null
            : state.activeChannelId;

        for (const m of freshMemberships) {
          const conv = next.get(m.channel.id);
          if (conv) {
            next.set(m.channel.id, { ...conv, membership: m });
          }
        }

        return {
          conversations: next,
          activeChannelId: newActiveChannelId,
          ...resetSelectionState(),
        };
      });
    } catch (error) {
      toast.error(String(error));
    }
  };

const unarchiveSelection =
  (set: SetState) =>
  async (channelIds: Set<string>): Promise<void> => {
    const { chat } = useChatStore.getState();
    if (!chat || channelIds.size === 0) return;

    try {
      const { memberships } = await chat.currentUser.getMemberships();

      await chat.sdk.objects.setMemberships({
        channels: buildUpdatedChannels(memberships, channelIds, false),
      });

      const { memberships: freshMemberships } = await chat.currentUser.getMemberships();

      set(state => {
        const next = cloneConversations(state);

        for (const membership of freshMemberships) {
          const conv = next.get(membership.channel.id);
          if (conv) {
            next.set(membership.channel.id, { ...conv, membership });
          }
        }

        return {
          conversations: next,
          ...resetSelectionState(),
        };
      });
    } catch (error) {
      toast.error(String(error));
    }
  };

const leaveSelection =
  (set: SetState) =>
  async (channelIds: Set<string>): Promise<void> => {
    const { chat } = useChatStore.getState();
    if (!chat || channelIds.size === 0) return;

    try {
      const { memberships } = await chat.currentUser.getMemberships();
      await Promise.all(
        memberships
          .filter(membership => channelIds.has(membership.channel.id))
          .map(membership =>
            chat.updateChannel(membership.channel.id, {
              custom: {
                ...((membership.channel.custom as Record<string, unknown> | null) ?? {}),
                closed: true,
              },
            })
          )
      );
      await chat.sdk.objects.removeMemberships({
        channels: Array.from(channelIds),
      });

      set(state => {
        const next = cloneConversations(state);
        let newActiveChannelId = state.activeChannelId;

        for (const id of channelIds) {
          next.delete(id);
          if (newActiveChannelId === id) {
            newActiveChannelId = null;
          }
        }

        return {
          conversations: next,
          activeChannelId: newActiveChannelId,
          ...resetSelectionState(),
        };
      });
    } catch (error) {
      toast.error(String(error));
    }
  };

export const useConversationsStore = create<ConversationsStore>()((set, get) => ({
  conversations: new Map(),
  activeChannelId: null,
  isLoading: true,
  isLoadingConversation: false,
  isCreatingConversation: false,
  isTyping: false,
  isOtherUserOnline: false,
  isOpenInfo: getDefaultIsOpenInfoFromViewport(),
  isSelectionMode: false,
  selectedChannelIds: new Set<string>(),
  setActiveChannel: (channelId: string | null): void => set({ activeChannelId: channelId }),
  setIsOpenInfo: (value: boolean): void => set({ isOpenInfo: value }),
  setIsTyping: (value: boolean): void => set({ isTyping: value }),
  setIsOtherUserOnline: (value: boolean): void => set({ isOtherUserOnline: value }),
  toggleSelectionMode: toggleSelectionMode(set),
  toggleChannelSelection: toggleChannelSelection(set),
  archiveSelection: archiveSelection(set),
  unarchiveSelection: unarchiveSelection(set),
  leaveSelection: leaveSelection(set),
  loadConversations: loadConversations(set),
  createConversation: createConversation(set),
  addConversation: (conversation: Conversation): void =>
    set(state => ({
      conversations: new Map(state.conversations).set(conversation.channel.id, conversation),
    })),
  leaveConversation: leaveConversation(set),
  archiveConversation: archiveConversation(set, get),
  unarchiveConversation: unarchiveConversation(set, get),
  updateUnreadCount: updateUnreadCount(set, get),
  resetUnreadCount: resetUnreadCount(set, get),
}));

export const useActiveConversation = (): Conversation | null =>
  useConversationsStore(state => {
    if (!state.activeChannelId) return null;
    return state.conversations.get(state.activeChannelId) ?? null;
  });

export const useActiveOtherUserId = (): string | undefined =>
  useConversationsStore(state =>
    state.activeChannelId ? state.conversations.get(state.activeChannelId)?.otherUser.id : undefined
  );

export const useActiveOtherUserName = (): string | undefined =>
  useConversationsStore(state =>
    state.activeChannelId
      ? state.conversations.get(state.activeChannelId)?.otherUser.name
      : undefined
  );

export const useConversationsList = (): Conversation[] =>
  useConversationsStore(useShallow(state => Array.from(state.conversations.values())));

export const useActiveConversationsList = (): Conversation[] =>
  useConversationsStore(
    useShallow(state =>
      Array.from(state.conversations.values())
        .filter(conversation => conversation.membership?.custom?.archived !== true)
        .sort((a, b) => (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0))
    )
  );

export const useArchivedConversationsList = (): Conversation[] =>
  useConversationsStore(
    useShallow(state =>
      Array.from(state.conversations.values())
        .filter(conversation => conversation.membership?.custom?.archived === true)
        .sort((a, b) => (b.lastMessageAt ?? 0) - (a.lastMessageAt ?? 0))
    )
  );

export const useChannelIds = (): string[] =>
  useConversationsStore(useShallow(state => Array.from(state.conversations.keys())));

type UnreadConversation = { id: string; name: string; userId: string };

export const useUnreadConversations = (): UnreadConversation[] => {
  const serialized = useConversationsStore(state =>
    JSON.stringify(
      Array.from(state.conversations.values())
        .filter(conversation => conversation.unreadCount > 0)
        .map(conversation => ({
          id: conversation.channel.id,
          name: conversation.otherUser.name ?? '',
          userId: conversation.otherUser.id,
        }))
        .sort((a, b) => a.id.localeCompare(b.id))
    )
  );
  return JSON.parse(serialized) as UnreadConversation[];
};
